import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Palette, Upload, Save, Eye, Globe, Mail, Phone, MapPin } from 'lucide-react';

export default function SchoolBranding() {
  const [branding, setBranding] = useState({
    school_name: '', tagline: '', primary_color: '#14b8a6', secondary_color: '#0f172a',
    accent_color: '#f59e0b', address: '', website: '', support_email: '', support_phone: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/school/branding').then(res => {
      const d = res.data.data;
      setBranding(prev => ({ ...prev, ...d }));
      if (d.logo_path) {
        api.get(`/files/${d.logo_path}`, { responseType: 'blob' }).then(r => {
          setLogoPreview(URL.createObjectURL(r.data));
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { logo_path, logo_updated_at, tenant_id, created_at, updated_at, logo_url, ...payload } = branding;
      await api.put('/school/branding', payload);
      toast.success('Branding saved successfully');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/school/branding/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoPreview(URL.createObjectURL(file));
      toast.success('Logo uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const update = (field, value) => setBranding(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-6" data-testid="school-branding-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-md shadow-violet-500/20">
            <Palette className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">School Branding</h1>
            <p className="text-sm text-slate-500">Customize your school's identity on the login page</p>
          </div>
        </div>
        <Button className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-md shadow-teal-500/20" onClick={handleSave} disabled={saving} data-testid="save-branding-btn">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo */}
        <div className="glass-card p-6 flex flex-col items-center gap-4" data-testid="logo-section">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider self-start">School Logo</p>
          <div className="h-32 w-32 rounded-2xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden cursor-pointer hover:border-teal-400 transition-colors" onClick={() => fileRef.current?.click()} data-testid="logo-upload-area">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="h-full w-full object-contain p-2" />
            ) : (
              <div className="text-center">
                <Upload className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-[10px] text-slate-400">Click to upload</p>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
          <Button variant="outline" size="sm" className="border-slate-200 text-slate-600 rounded-lg" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
            {uploading ? 'Uploading...' : 'Upload Logo'}
          </Button>
          <p className="text-[10px] text-slate-400 text-center">PNG, JPG or WebP. Max 5MB. Recommended: 512x512px</p>
        </div>

        {/* School Info */}
        <div className="lg:col-span-2 glass-card p-6 space-y-5" data-testid="branding-form">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">School Information</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold">School Name</Label>
              <Input value={branding.school_name} onChange={(e) => update('school_name', e.target.value)} className="bg-white border-slate-200 text-slate-800 rounded-xl" placeholder="Springfield Academy" data-testid="school-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold">Tagline</Label>
              <Input value={branding.tagline || ''} onChange={(e) => update('tagline', e.target.value)} className="bg-white border-slate-200 text-slate-800 rounded-xl" placeholder="Excellence in Education" data-testid="tagline-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold flex items-center gap-1"><Globe className="h-3 w-3" /> Website</Label>
              <Input value={branding.website || ''} onChange={(e) => update('website', e.target.value)} className="bg-white border-slate-200 text-slate-800 rounded-xl" placeholder="https://school.edu" data-testid="website-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold flex items-center gap-1"><MapPin className="h-3 w-3" /> Address</Label>
              <Input value={branding.address || ''} onChange={(e) => update('address', e.target.value)} className="bg-white border-slate-200 text-slate-800 rounded-xl" placeholder="123 Education St." data-testid="address-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold flex items-center gap-1"><Mail className="h-3 w-3" /> Support Email</Label>
              <Input value={branding.support_email || ''} onChange={(e) => update('support_email', e.target.value)} className="bg-white border-slate-200 text-slate-800 rounded-xl" placeholder="support@school.edu" data-testid="support-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold flex items-center gap-1"><Phone className="h-3 w-3" /> Support Phone</Label>
              <Input value={branding.support_phone || ''} onChange={(e) => update('support_phone', e.target.value)} className="bg-white border-slate-200 text-slate-800 rounded-xl" placeholder="+91 98765 43210" data-testid="support-phone-input" />
            </div>
          </div>

          <div className="pt-2">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Brand Colors</p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { key: 'primary_color', label: 'Primary', desc: 'Main accent color' },
                { key: 'secondary_color', label: 'Secondary', desc: 'Login background' },
                { key: 'accent_color', label: 'Accent', desc: 'Highlight color' },
              ].map(c => (
                <div key={c.key} className="space-y-2" data-testid={`color-${c.key}`}>
                  <Label className="text-slate-600 text-xs font-semibold">{c.label}</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={branding[c.key] || '#14b8a6'} onChange={(e) => update(c.key, e.target.value)} className="h-9 w-9 rounded-lg cursor-pointer bg-transparent border border-slate-200" />
                    <Input value={branding[c.key] || ''} onChange={(e) => update(c.key, e.target.value)} className="bg-white border-slate-200 text-slate-800 rounded-xl text-xs flex-1" placeholder="#14b8a6" />
                  </div>
                  <p className="text-[10px] text-slate-400">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Live Preview */}
      <div className="glass-card p-6" data-testid="branding-preview">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="h-4 w-4 text-teal-500" />
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Login Page Preview</p>
        </div>
        <div className="rounded-2xl border border-slate-200 overflow-hidden" style={{ background: branding.secondary_color || '#0c1222' }}>
          <div className="flex items-center justify-center py-12 px-6">
            <div className="w-full max-w-xs text-center">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="h-16 w-16 object-contain mx-auto mb-3 rounded-xl" />
              ) : (
                <div className="h-16 w-16 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: branding.primary_color || '#14b8a6' }}>
                  <span className="text-white text-2xl font-bold">{(branding.school_name || 'S').charAt(0)}</span>
                </div>
              )}
              <h2 className="text-lg font-bold text-white mb-0.5">{branding.school_name || 'Your School'}</h2>
              {branding.tagline && <p className="text-xs text-white/50 mb-4">{branding.tagline}</p>}
              <div className="space-y-3 mt-4">
                <div className="h-10 rounded-xl bg-white/[0.05] border border-white/[0.1]" />
                <div className="h-10 rounded-xl bg-white/[0.05] border border-white/[0.1]" />
                <div className="h-10 rounded-xl" style={{ background: branding.primary_color || '#14b8a6' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
